import {createSlice, type PayloadAction} from "@reduxjs/toolkit";
import {type UserProfile} from "oidc-client-ts";
import {type MyAccount} from "../model/user/Account.ts";
import type {UserScope, UserType} from "../model/user/UserScope.ts";
import type {SupportContext} from "../model/user/SupportContext.ts";

export interface AuthReducerState {
    errorMessage: string;
    profile: UserProfile | null;
    userId: string | null;
    myAccount: MyAccount | null;
    userScope: UserScope | null;
    /** The scope from the token while a support context replaces userScope. */
    ownScope: UserScope | null;
    supportContext: SupportContext | null;
    accessToken?: string;
    agreementFaulty: boolean;
}

const initialState: AuthReducerState = {
    errorMessage: "",
    profile: null,
    userId: null,
    myAccount: null,
    userScope: null,
    ownScope: null,
    supportContext: null,
    accessToken: undefined,
    agreementFaulty: false
};

interface LoginSuccessPayload {
    profile: UserProfile;
    accessToken: string;
    userScope: UserScope;
}

// Only a scope without any restriction is unlimited. Distributor-scoped users
// (manufacturer staff) have no cockpit pages yet and must not get admin routes.
const deriveUserType = (scope: UserScope): UserType => {
    if (scope.garageNo) return "garage";
    if (scope.garageGroupNo) return "group";
    if (scope.distributors?.length) return "unknown";
    return "admin";
};

// Support users see the cockpit as the garage or group of their context; the
// backend applies the same scope through the context header (#56913).
const scopeOf = (own: UserScope, context: SupportContext): UserScope => ({
    ...own,
    garageNo: context.type === "garage" ? {index: context.no} : null,
    garageGroupNo: context.type === "group" ? {index: context.no} : null,
    distributors: [],
    type: context.type,
});

export const authReducer = createSlice({
    name: "auth",
    initialState,
    reducers: {
        loginSuccess: (state, action: PayloadAction<LoginSuccessPayload>) => {
            state.profile = action.payload.profile;
            state.accessToken = action.payload.accessToken;
            const own = action.payload.userScope
                ? {...action.payload.userScope, type: deriveUserType(action.payload.userScope)}
                : action.payload.userScope;
            // a token refresh must not drop an active support context
            state.ownScope = own;
            state.userScope = own && state.supportContext ? scopeOf(own, state.supportContext) : own;
        },
        startSupportContext: (state, action: PayloadAction<SupportContext>) => {
            if (!state.ownScope) return;
            state.supportContext = action.payload;
            state.userScope = scopeOf(state.ownScope, action.payload);
        },
        endSupportContext: state => {
            state.supportContext = null;
            state.userScope = state.ownScope;
        },
        setAccount: (state, action: PayloadAction<MyAccount>) => {
            if (!state.myAccount) state.myAccount = action.payload;
        },
        setAgreementFaulty: (state, action: PayloadAction<boolean>) => {
            state.agreementFaulty = action.payload;
        },
        setUserDetails: (state, action: PayloadAction<MyAccount["userDetails"]>) => {
            state.myAccount = state.myAccount
                ? {
                    ...state.myAccount,
                    userDetails: action.payload
                }
                : null;
        },
        loginFailure: (state, action: PayloadAction<{ errorMessage: string }>) => {
            state.errorMessage = action.payload.errorMessage;
        },
        logoutSuccess: () => initialState,
    }
});

export const authReducerActions = authReducer.actions;
export default authReducer.reducer;
